// API Configuration
// This file manages the base URL for all API calls

// Determine the API base URL based on environment
const getApiBaseUrl = () => {
  // In development, use localhost:8000
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  }
  
  // In production, use the current host
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Fallback to current origin for production builds
  return `${window.location.origin}`;
};

export const API_BASE_URL = getApiBaseUrl();

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: `${API_BASE_URL}/api/auth/login/`,
  AUTH_REGISTER: `${API_BASE_URL}/api/auth/register/`,
  AUTH_LOGOUT: `${API_BASE_URL}/api/auth/logout/`,
  AUTH_SESSIONS: `${API_BASE_URL}/api/auth/sessions/`,
  AUTH_SESSION_DETAIL: (id) => `${API_BASE_URL}/api/auth/sessions/${id}/`,
  AUTH_SESSIONS_REVOKE_OTHERS: `${API_BASE_URL}/api/auth/sessions/revoke-others/`,
  AUTH_ME: `${API_BASE_URL}/api/auth/me/`,
  AUTH_ME_UPDATE: `${API_BASE_URL}/api/auth/me/update/`,
  AUTH_PREFERENCES: `${API_BASE_URL}/api/auth/preferences/`,
  AUTH_FORGOT_PASSWORD: `${API_BASE_URL}/api/auth/forgot-password/`,
  AUTH_RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password/`,
  AUTH_USERS: `${API_BASE_URL}/api/auth/users/`,
  AUTH_USER_DETAIL: (id) => `${API_BASE_URL}/api/auth/users/${id}/`,
  AUTH_USER_INVITE: (id) => `${API_BASE_URL}/api/auth/users/${id}/invite/`,
  FIELD_CHILL: (id) => `${API_BASE_URL}/api/fields/${id}/chill/`,
  FIELDS_IMPORT_BOUNDARIES: `${API_BASE_URL}/api/fields/import-boundaries/`,
  FIELDS_CADASTRE: `${API_BASE_URL}/api/fields/cadastre/`,
  // Fields
  FIELDS: `${API_BASE_URL}/api/fields/`,
  // Admin: editable per-texture VWCw soil-moisture threshold profiles.
  SOIL_PROFILES: `${API_BASE_URL}/api/fields/soil-profiles/`,
  HISTORY: `${API_BASE_URL}/api/history/`,
  HISTORY_UNDO: `${API_BASE_URL}/api/history/undo/`,
  HISTORY_ACTIVITY: `${API_BASE_URL}/api/history/activity/`,
  SOIL_PROFILE_DETAIL: (slug) => `${API_BASE_URL}/api/fields/soil-profiles/${slug}/`,
  SOIL_PROFILE_RESET: (slug) => `${API_BASE_URL}/api/fields/soil-profiles/${slug}/reset/`,
  FIELDS_ALL_WITH_DATA: `${API_BASE_URL}/api/fields/all-with-data`,
  FIELD_VIEW_DATA: (id) => `${API_BASE_URL}/api/fields/${id}/view`,
  FIELD_DETAIL: (id) => `${API_BASE_URL}/api/fields/${id}/`,
  FIELD_STAGE: (id) => `${API_BASE_URL}/api/fields/${id}/stage/`,
  FIELD_ORIGIN: (id) => `${API_BASE_URL}/api/fields/${id}/origin/`,
  FIELD_RESTORE: (id) => `${API_BASE_URL}/api/fields/${id}/restore/`,
  // In-app Dashboard tab — same numbers as the emailed client PDF.
  FIELD_VWCW_DASHBOARD: (id) => `${API_BASE_URL}/api/fields/${id}/vwcw-dashboard/`,
  // The live recommendation per owned field, shown on opening the app.
  FIELD_RECOMMENDATIONS: `${API_BASE_URL}/api/fields/recommendations/`,
  FIELD_RECOMPUTE_SOIL_TYPE: (id) => `${API_BASE_URL}/api/fields/${id}/recompute-soil-type/`,
  // Per-field NDVI image — replaces the per-tile WMS proxy. One Sentinel
  // call per field per Sentinel-2 revisit, served from DB.
  FIELD_NDVI_IMAGE: (id) => `${API_BASE_URL}/api/fields/${id}/ndvi-image/`,
  FIELD_NDVI_IMAGE_RAW: (id, scene_date) =>
    `${API_BASE_URL}/api/fields/${id}/ndvi-image/${scene_date}/raw/`,
  FIELD_ZONES: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/zones/`,
  FIELD_ZONE_DETAIL: (fieldId, zoneId) => `${API_BASE_URL}/api/fields/${fieldId}/zones/${zoneId}/`,
  FIELD_SEASONS: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/seasons/`,
  FIELD_SEASON_DETAIL: (fieldId, id) => `${API_BASE_URL}/api/fields/${fieldId}/seasons/${id}/`,
  FIELD_NOTES: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/notes/`,
  FIELD_NOTE_DETAIL: (fieldId, noteId) => `${API_BASE_URL}/api/fields/${fieldId}/notes/${noteId}/`,
  ALL_FIELD_NOTES: `${API_BASE_URL}/api/fields/notes/`,
  FIELD_NDVI_THUMBNAIL: (fieldId, type = 'ndvi', date = '') => `${API_BASE_URL}/api/fields/${fieldId}/ndvi-thumbnail/?type=${type}${date ? `&date=${date}` : ''}`,
  
  // Weather
  WEATHER_CURRENT: `${API_BASE_URL}/api/weather/current/`,
  WEATHER_FORECAST: `${API_BASE_URL}/api/weather/forecast/`,
  WEATHER_DIAGNOSTICS: `${API_BASE_URL}/api/weather/diagnostics/`,
  WEATHER_HISTORY: `${API_BASE_URL}/api/weather/history/`,
  WEATHER_STATIONS: `${API_BASE_URL}/api/weather/stations/`,
  WEATHER_STATION: (id) => `${API_BASE_URL}/api/weather/stations/${id}/`,
  WEATHER_STATION_POLL: (id) => `${API_BASE_URL}/api/weather/stations/${id}/poll/`,
  
  // Sensors
  SENSORS_DEVICES: `${API_BASE_URL}/api/sensors/devices/`,
  SENSOR_DEVICE_DETAIL: (id) => `${API_BASE_URL}/api/sensors/devices/${id}/`,
  SENSORS_READINGS: `${API_BASE_URL}/api/sensors/readings/`,
  SENSORS_TIMESERIES: `${API_BASE_URL}/api/sensors/readings/timeseries/`,
  SENSORS_PROFILE: `${API_BASE_URL}/api/sensors/readings/profile/`,
  SENSORS_STATISTICS: `${API_BASE_URL}/api/sensors/readings/statistics/`,
  SENSOR_DEVICE_READINGS: (id) => `${API_BASE_URL}/api/sensors/devices/${id}/readings/`,
  SENSOR_HUBS: `${API_BASE_URL}/api/sensors/hubs/`,
  
  // Irrigation
  IRRIGATION_TASKS: `${API_BASE_URL}/api/irrigation/tasks/`,
  IRRIGATION_TASK_DETAIL: (id) => `${API_BASE_URL}/api/irrigation/tasks/${id}/`,
  IRRIGATION_TASK_ACTION: (id, action) => `${API_BASE_URL}/api/irrigation/tasks/${id}/${action}/`,
  IRRIGATION_TODAY: `${API_BASE_URL}/api/irrigation/today/`,
  IRRIGATION_ZONES: `${API_BASE_URL}/api/irrigation/zones/`,
  MANUAL_START_ZONE: (zoneId) => `${API_BASE_URL}/api/irrigation/manual/zone/${zoneId}/start/`,
  MANUAL_STOP_ZONE: (zoneId) => `${API_BASE_URL}/api/irrigation/manual/zone/${zoneId}/stop/`,
  MANUAL_ZONE_STATUS: (zoneId) => `${API_BASE_URL}/api/irrigation/manual/zone/${zoneId}/status/`,
  MANUAL_ACTIVE_IRRIGATIONS: `${API_BASE_URL}/api/irrigation/manual/active_irrigations/`,
  EMERGENCY_STOP: `${API_BASE_URL}/api/irrigation/emergency-stop/`,
  ZONE_CONTROL_MODE: (zoneId) => `${API_BASE_URL}/api/irrigation/zones/${zoneId}/control-mode/`,

  // DSS Debug
  DSS_DEBUG_ZONE: (zoneId) => `${API_BASE_URL}/api/irrigation/dss-debug/${zoneId}/`,
  DSS_DEBUG_FIELD: (fieldId) => `${API_BASE_URL}/api/irrigation/dss-debug/field/${fieldId}/`,
  DSS_TRIGGER_FIELD: (fieldId) => `${API_BASE_URL}/api/irrigation/dss-trigger/${fieldId}/`,

  // Alerts
  ALERTS_ACTIVE: `${API_BASE_URL}/api/alerts/active/`,
  ALERTS_ALL: `${API_BASE_URL}/api/alerts/`,
  ALERTS_LIST: `${API_BASE_URL}/api/alerts/alerts/`,
  ALERT_DETAIL: (id) => `${API_BASE_URL}/api/alerts/alerts/${id}/`,
  ALERT_ACKNOWLEDGE: (id) => `${API_BASE_URL}/api/alerts/${id}/acknowledge/`,
  ALERT_REQUEST_HELP: `${API_BASE_URL}/api/alerts/request-help/`,
  ALERT_RULES: `${API_BASE_URL}/api/alerts/rules/`,
  ALERT_RULE_DETAIL: (id) => `${API_BASE_URL}/api/alerts/rules/${id}/`,

  // Weekly Schedules
  WEEKLY_SCHEDULES: `${API_BASE_URL}/api/irrigation/weekly-schedules/`,
  WEEKLY_SCHEDULE_DETAIL: (id) => `${API_BASE_URL}/api/irrigation/weekly-schedules/${id}/`,
  // DSS Recommendations & Backtest
  DSS_RECOMMENDATIONS: `${API_BASE_URL}/api/irrigation/dss-recommendations/`,
  DSS_RECOMMENDATIONS_BY_FIELD: (fieldId) => `${API_BASE_URL}/api/irrigation/dss-recommendations/?field=${fieldId}`,
  DSS_RECOMMEND_WEEK: `${API_BASE_URL}/api/irrigation/dss-recommend-week/`,
  DSS_RECOMMENDATION_ACTION: (id, action) => `${API_BASE_URL}/api/irrigation/dss-recommendations/${id}/${action}/`,
  DSS_RECOMMENDATIONS_BULK_ACCEPT: `${API_BASE_URL}/api/irrigation/dss-recommendations/accept-all/`,
  DSS_RESET_DEMO: `${API_BASE_URL}/api/irrigation/reset-demo/`,
  DSS_BACKTEST: `${API_BASE_URL}/api/irrigation/backtest/`,
  DSS_FEATURE_IMPORTANCE: `${API_BASE_URL}/api/irrigation/dss-feature-importance/`,
  DSS_SIMULATION_CONTEXT: `${API_BASE_URL}/api/irrigation/dss-simulation-context/`,
  DSS_PLAYGROUND: `${API_BASE_URL}/api/irrigation/dss-playground/`,
  DSS_FORECAST: `${API_BASE_URL}/api/irrigation/dss-forecast/`,
  // Audit trail / history
  IRRIGATION_PLANS: `${API_BASE_URL}/api/irrigation/plans/`,
  IRRIGATION_HISTORY: `${API_BASE_URL}/api/irrigation/history/`,
  ZONE_SOIL_RESPONSES: (zoneId) => `${API_BASE_URL}/api/irrigation/zones/${zoneId}/soil-responses/`,
  ZONE_DSS_FORECAST: (zoneId) => `${API_BASE_URL}/api/irrigation/zones/${zoneId}/dss-forecast/`,

  // NDVI Satellite
  NDVI_PROXY: `${API_BASE_URL}/api/fields/ndvi-proxy/`,
  NDVI_ZONE_STATS: (fieldId, zoneId) => `${API_BASE_URL}/api/fields/${fieldId}/zones/${zoneId}/ndvi-stats/`,
  NDVI_FIELD_SUMMARY: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/ndvi-summary/`,
  NDVI_ANALYTICS: `${API_BASE_URL}/api/fields/ndvi-analytics/`,

  // Irrigation by zone
  IRRIGATION_BY_ZONE: `${API_BASE_URL}/api/irrigation/irrigation-by-zone/`,

  // Dashboard
  DASHBOARD_SUMMARY: `${API_BASE_URL}/api/dashboard/summary/`,
  
  // Analytics
  ANALYTICS_SUMMARY: `${API_BASE_URL}/api/analytics/summary/`,
  PRODUCTION_SUMMARY: `${API_BASE_URL}/api/analytics/production/`,
  ADMIN_SYSTEM_ANALYTICS: `${API_BASE_URL}/api/analytics/admin-system/`,
  OPERATIONS: `${API_BASE_URL}/api/analytics/operations/`,

  // Competitive features — new endpoints
  DISEASE_RISK: `${API_BASE_URL}/api/irrigation/disease-risk/`,
  FARM_EVENTS: `${API_BASE_URL}/api/irrigation/farm-events/`,
  FARM_EVENT_DETAIL: (id) => `${API_BASE_URL}/api/irrigation/farm-events/${id}/`,
  FARM_EVENT_UPLOAD_PHOTO: (id) => `${API_BASE_URL}/api/irrigation/farm-events/${id}/upload-photo/`,
  FARM_EVENT_REMOVE_PHOTO: (id) => `${API_BASE_URL}/api/irrigation/farm-events/${id}/remove-photo/`,
  SPRAY_WINDOW: `${API_BASE_URL}/api/weather/spray-window/`,
  PHENOLOGY: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/phenology/`,
  FRUIT_SIZING: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/fruit-sizing/`,
  OPEKEPE_EXPORT: `${API_BASE_URL}/api/fields/opekepe-export/`,
  FIELD_PRODUCTION: (fieldId) => `${API_BASE_URL}/api/fields/${fieldId}/production/`,
  FIELD_PRODUCTION_DETAIL: (fieldId, id) => `${API_BASE_URL}/api/fields/${fieldId}/production/${id}/`,
  TOUR_SEED: `${API_BASE_URL}/api/tour/seed/`,
  TOUR_EXIT: `${API_BASE_URL}/api/tour/exit/`,
  TOUR_RESET: `${API_BASE_URL}/api/tour/reset/`,
  TOUR_STATUS: `${API_BASE_URL}/api/tour/status/`,
  ALERTS_BROADCAST: `${API_BASE_URL}/api/alerts/broadcast/`,
  // Edge device admin
  EDGE_DEVICES: `${API_BASE_URL}/api/edge/admin/devices/`,
  EDGE_DEVICE_DETAIL: (deviceId) => `${API_BASE_URL}/api/edge/admin/devices/${deviceId}/`,
  EDGE_DEVICE_STATUS: (deviceId) => `${API_BASE_URL}/api/edge/admin/devices/${deviceId}/status/`,
  EDGE_TEST_ZONE: `${API_BASE_URL}/api/edge/admin/test-zone/`,
  EDGE_COMMAND_STATUS: (commandId) => `${API_BASE_URL}/api/edge/admin/commands/${commandId}/status/`,

  // Unified device layer — every device, one shape, plain-language health.
  DEVICES_OVERVIEW: `${API_BASE_URL}/api/devices/`,

  REPORTS_LIST: `${API_BASE_URL}/api/reports/`,
  REPORTS_GENERATE: `${API_BASE_URL}/api/reports/generate/`,
  REPORTS_DOWNLOAD: (filename) => `${API_BASE_URL}/api/reports/${encodeURIComponent(filename)}/download/`,
  DEVICES_UNBOUND_ZONES: `${API_BASE_URL}/api/devices/unbound-zones/`,
  // Which measuring station feeds which field — the row that connects a field
  // to its data. See fields/views/station_assignments.py.
  STATION_ASSIGNMENTS: `${API_BASE_URL}/api/devices/stations/`,
  STATION_ASSIGNMENT_DETAIL: (id) => `${API_BASE_URL}/api/devices/stations/${id}/`,
  DEVICE_BINDINGS: `${API_BASE_URL}/api/devices/bindings/`,
  DEVICE_BINDING_DETAIL: (id) => `${API_BASE_URL}/api/devices/bindings/${id}/`,
  DEVICE_BINDING_TEST: (id) => `${API_BASE_URL}/api/devices/bindings/${id}/test/`,
  IOT_DEVICES: `${API_BASE_URL}/api/devices/iot-devices/`,
  SENTEK_LOGGERS: `${API_BASE_URL}/api/devices/sentek-loggers/`,
  SENTEK_LOGGERS_TEST: `${API_BASE_URL}/api/devices/sentek-loggers/test/`,
  IOT_DEVICE_DETAIL: (deviceId) => `${API_BASE_URL}/api/devices/iot-devices/${deviceId}/`,
  READINGS_SERIES: `${API_BASE_URL}/api/readings/series/`,
  RECOMMENDATION_ANALYTICS: `${API_BASE_URL}/api/analytics/recommendations/`,
  IOT_DEVICE_CONNECT: (deviceId) => `${API_BASE_URL}/api/devices/iot-devices/${deviceId}/connect/`,

  // Messaging / support inbox
  SUPPORT_CREATE:        `${API_BASE_URL}/api/messaging/messages/create/`,
  MY_MESSAGES:           `${API_BASE_URL}/api/messaging/my-messages/`,
  SUPPORT_MESSAGES:      `${API_BASE_URL}/api/messaging/messages/`,
  SUPPORT_DETAIL:        (id) => `${API_BASE_URL}/api/messaging/messages/${id}/`,
  SUPPORT_REPLY:         (id) => `${API_BASE_URL}/api/messaging/messages/${id}/reply/`,
  SUPPORT_UNREAD_COUNT:  `${API_BASE_URL}/api/messaging/unread-count/`,
};

export default API_ENDPOINTS;
